import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import Checkbox from "@material-ui/core/Checkbox";
import Avatar from "@material-ui/core/Avatar";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import SendIcon from "@material-ui/icons/Send";
import Paper from "@material-ui/core/Paper";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { FormControlLabel, Switch, Typography, Fade } from "@material-ui/core";
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";

const useStyles = makeStyles((theme) => ({
	dialogPaper: {
		backgroundColor: theme.palette.background.paper,
		borderRadius: 16,
		width: 420,
		maxWidth: "100%",
		overflow: "hidden",
	},
	header: {
		display: "flex",
		alignItems: "center",
		padding: "16px",
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.text.primary,
		"& h2": {
			fontSize: "1.1rem",
			marginLeft: "8px",
            fontWeight: 500,
		}
	},
	searchContainer: {
		padding: "0 16px 12px",
	},
	searchInputWrapper: {
		display: "flex",
		alignItems: "center",
		backgroundColor: theme.palette.inputdigita,
		borderRadius: 24,
		padding: "6px 14px",
		border: `1px solid ${theme.palette.divider}`,
	},
	searchInput: {
		flex: 1,
		marginLeft: 8,
		fontSize: 15,
		color: theme.palette.text.primary,
	},
	listContainer: {
		minHeight: 320,
		maxHeight: 420,
		overflowY: "auto",
		paddingTop: 0,
		...theme.scrollbarStyles,
	},
	listItem: {
		cursor: "pointer",
		transition: "background-color 0.15s",
		"&:hover": {
			backgroundColor: theme.palette.optionsBackground,
		}
	},
	avatar: {
		width: 44,
		height: 44,
		color: "#fff",
		fontWeight: "bold",
	},
	footer: {
		backgroundColor: theme.palette.background.default,
		padding: "12px 16px",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	sendButton: {
		backgroundColor: "#00a884",
		color: "#fff",
		width: 46,
		height: 46,
		borderRadius: "50%",
		"&:hover": {
			backgroundColor: "#06cf9c",
		},
		"&:disabled": {
			backgroundColor: theme.palette.action.disabledBackground,
		}
	}
}));

const ForwardMessageModal = ({ messages, onClose, modalOpen }) => {
	const classes = useStyles();
	const [optionsContacts, setOptionsContacts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContacts, setSelectedContacts] = useState([]);
	const { user } = useContext(AuthContext);
	const [sending, setSending] = useState(false);
	const [messageSending, setMessageSending] = useState('');
	const [signMessage, setSignMessage] = useState(true);
    const { setShowSelectMessageCheckbox, setSelectedMessages } = useContext(ForwardMessageContext);

	useEffect(() => {
		if (!modalOpen) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam },
					});
					setOptionsContacts(data.contacts);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	const sleep = (ms) => {
		return new Promise(resolve => setTimeout(resolve, ms));
	};

	const handleForwardMessage = async () => {
		setSending(true);
		for (const message of messages) {
			try {
				setMessageSending(message.id);
				for (const contactL of selectedContacts) {
					await api.post('/message/forward', { messageId: message.id, contactId: contactL.id, signMessage: signMessage });
					await sleep(900);
				}
			} catch (error) {
				toastError(error);
			}
		}
		setSending(false);
		handleClose();
        if (setShowSelectMessageCheckbox) setShowSelectMessageCheckbox(false);
        if (setSelectedMessages) setSelectedMessages([]);
	};

	const toggleContactSelection = (contact) => {
		const index = selectedContacts.findIndex(c => c.id === contact.id);
		if (index > -1) {
			const newSelected = [...selectedContacts];
			newSelected.splice(index, 1);
			setSelectedContacts(newSelected);
		} else {
			setSelectedContacts([...selectedContacts, contact]);
		}
	};

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContacts([]);
		setSending(false);
	};

	return (
		<Dialog 
			open={modalOpen} 
			onClose={handleClose} 
			classes={{ paper: classes.dialogPaper }}
			maxWidth="sm"
		>
			<div className={classes.header}>
				<IconButton size="small" onClick={handleClose}>
					<CloseIcon style={{ color: "inherit", fontSize: 24 }} />
				</IconButton>
				<Typography variant="h2">Encaminhar mensagens para</Typography>
			</div>

			<div className={classes.searchContainer}>
				<div className={classes.searchInputWrapper}>
					<SearchIcon style={{ color: "#8696a0" }} />
					<InputBase
						autoFocus
						className={classes.searchInput}
						placeholder="Pesquisar nome ou número"
						value={searchParam}
						onChange={e => setSearchParam(e.target.value)}
					/>
					{loading && <CircularProgress size={20} style={{ color: "#00a884" }} />}
				</div>
			</div>

			<DialogContent dividers style={{ padding: 0 }}>
				<List className={classes.listContainer}>
					{optionsContacts.map((contact) => {
						const isSelected = selectedContacts.some(c => c.id === contact.id);
						return (
							<ListItem 
								key={contact.id} 
								className={classes.listItem}
								onClick={() => toggleContactSelection(contact)}
							>
								<Checkbox
									checked={isSelected}
									color="primary"
									style={{ color: isSelected ? "#00a884" : undefined, padding: 8 }}
								/>
								<ListItemAvatar>
									<Avatar
										src={contact.profilePicUrl}
										className={classes.avatar}
										style={{ backgroundColor: generateColor(contact.number) }}
									>
										{getInitials(contact.name)}
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={<Typography variant="body2" style={{ fontSize: 16 }}>{contact.name}</Typography>}
									secondary={<Typography variant="caption" color="textSecondary" style={{ fontSize: 13 }}>{contact.number}</Typography>}
								/>
							</ListItem>
						);
					})}
					{!loading && optionsContacts.length === 0 && (
						<Typography style={{ padding: 20, textAlign: "center", color: "#8696a0", fontSize: 14 }}>
							Nenhum contato encontrado.
						</Typography>
					)}
				</List>
			</DialogContent>

			<Fade in={selectedContacts.length > 0}>
				<div className={classes.footer} style={{ display: selectedContacts.length > 0 ? "flex" : "none" }}>
					<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
						<IconButton size="small" onClick={() => setSelectedContacts([])}>
							<CloseIcon style={{ fontSize: 18, color: "inherit" }} />
						</IconButton>
						<Typography style={{ fontSize: 15 }}>
							{selectedContacts.length} {selectedContacts.length === 1 ? "selecionado" : "selecionados"}
						</Typography>
					</div>
					
					{sending ? (
						<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
							<CircularProgress size={24} color="primary" />
						</div>
					) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <FormControlLabel
                                style={{ marginRight: 7, color: "gray" }}
                                label={i18n.t("messagesInput.signMessage")}
                                labelPlacement="start"
                                control={
                                    <Switch
                                        size="small"
                                        checked={signMessage}
                                        onChange={(e) => {
                                            setSignMessage(e.target.checked);
                                        }}
                                        name="showAllTickets"
                                        color="primary"
                                    />
                                }
                            />
                            <IconButton className={classes.sendButton} onClick={handleForwardMessage}>
                                <SendIcon style={{ fontSize: 20, transform: "scaleX(-1)" }} />
                            </IconButton>
                        </div>
					)}
				</div>
			</Fade>
		</Dialog>
	);
};

export default ForwardMessageModal;